import React, { useContext, useEffect } from 'react';
import RadioInput from './RadioInput';
import { AutoFlex, Flex, Spacer } from '@adserve/adserve-react-components';
import { CampaignGoalEnum } from '../../../constants/Enums';
import { getGoalType } from '../../../services/UtilsService';
import LabelledSection from './LabelledSection/LabelledSection';
import { AirtimePlanningContext } from '../../../contexts/AirtimePlanningContext';
import WeeklyBudgetDropdown from './WeeklyBudgetDropdown/WeeklyBudgetDropdown';
import moment from 'moment';
import { goalPanelStyle } from '../CampaignBrief/CampaignBriefStyles';

const statusLabelStyle = {
    position: 'absolute',
    color: '#C40606',
    fontSize: 12,
    margin: '3px 0px 0px 10px'
};

const PlanningGoals = ({
    campaign,
    readOnly = false,
    updateCampaignGoal,
    data,
    onChange,
    errorValidationCampaignGoal,
    setErrorValidationCampaignGoal,
    isBurst = false,
    validationInputCampaignGoal
}) => {
    const { showInvalidatePlanMessage } = useContext(AirtimePlanningContext);

    const handleGoalSelect = (goal) => {
        if (goal.selected) return;

        // reset selected prop in the existing goals
        data?.forEach((d) => {
            if (d?.inputA?.goal !== goal?.goal) {
                d.inputA.selected = false;
                updateCampaignGoal(d?.inputA);
            }
            if (d?.inputB?.goal !== goal?.goal) {
                d.inputB.selected = false;
                updateCampaignGoal(d?.inputB);
            }
        });

        // set current goal as selected
        goal.selected = true;
        updateCampaignGoal(goal);
        checkInputError(goal.value);
    };

    const generateBudgetsArray = (weeklyValue) => {
        const firstMonday = new Date(campaign?.startDate);
        while (firstMonday.getDay() !== 1) {
            firstMonday.setDate(firstMonday.getDate() - 1);
        }

        let startDate = moment(firstMonday).utc(true).startOf('day');
        const endDate = moment(campaign?.endDate).utc(true).startOf('day');
        const diffInDays = endDate.diff(startDate, 'day');
        const weeks = Math.floor(diffInDays / 7) + 1;

        const weeklyBudgetArray = [];
        for (let index = 0; index < weeks; index++) {
            const week = { key: startDate.toDate(), value: weeklyValue };
            weeklyBudgetArray.push(week);
            startDate = startDate.add(7, 'days');
        }

        return weeklyBudgetArray;
    };

    const handleBudgetChanges = (goal, weeklyBudgets) => {
        if (goal.goal === CampaignGoalEnum.WEEKLYBUDGET) {
            goal.values = weeklyBudgets;

            const totalBudget = weeklyBudgets.reduce((a, b) => {
                return a + parseFloat(b.value);
            }, 0);

            goal.value = totalBudget;
            if (isBurst) campaign.budget = totalBudget;

            updateCampaignGoal(goal);
            checkInputError(goal.value);
            // Find the weekly budget box
            const element = document.getElementById(`${goal.goal.toLowerCase()}-input`);
            showInvalidatePlanMessage(element);
        }
    };

    const handleGoalValueChange = (goal, event) => {
        let value = event.target.value;

        if (goal.goal.includes('Reach') && value > 100) {
            value = 100;
        }
        if (goal.goal.includes('OTH') && (value > 10 || value === '10.')) {
            value = 10;
        }

        if (goal.goal === CampaignGoalEnum.WEEKLYBUDGET) {
            goal.values = generateBudgetsArray(value);

            const total = goal.values.reduce((a, b) => {
                return a + parseFloat(b.value);
            }, 0);

            goal.value = total;
            if (isBurst) campaign.budget = total;
        } else {
            goal.value = value;
        }

        updateCampaignGoal(goal);
        checkInputError(value);
        showInvalidatePlanMessage(event.target);
    };

    const handleBlur = (event) => {
        if (typeof onChange === 'function') {
            checkInputError(event.target.value);
        }
    };

    const checkInputError = (value) => {
        if (value === '' || value === null) {
            setErrorValidationCampaignGoal('Invalid Goal');
        } else {
            //only run burst budget check if there is not already a planning goal error displayed
            if (isBurst) onChange();
        }
    };

    return (
        <div style={goalPanelStyle}>
            <div
                style={{
                    fontSize: 15,
                    fontWeight: 500,
                    marginTop: 11,
                    marginBottom: 12,
                    marginLeft: 231
                }}
            >
                {window.translate(isBurst ? 'Campaign Burst Goal *' : 'Campaign Goal *')}
                {validationInputCampaignGoal !== true && (
                    <label style={statusLabelStyle}>{errorValidationCampaignGoal}</label>
                )}
            </div>
            <div style={{ marginLeft: 230, width: 1390 }}>
                <AutoFlex style={{ width: '105%' }}>
                    {data?.map((d, i) => {
                        return (
                            <LabelledSection
                                id={`${d.id.toLowerCase()}-input`}
                                key={`${d.sectionTitle}-section${i}`}
                                height={95}
                                width={273}
                                text={d.sectionTitle}
                            >
                                <Flex
                                    style={{
                                        marginLeft: 9,
                                        justifyContent: d?.inputA?.goal === CampaignGoalEnum.BUDGET ? 'center' : 'unset'
                                    }}
                                >
                                    {d?.inputA?.goal === CampaignGoalEnum.BUDGET ? (
                                        <>
                                            <RadioInput
                                                key={d?.inputA?.goal}
                                                id={`${d?.inputA?.goal.toLowerCase()}-input`}
                                                name={d?.inputA?.goal}
                                                title={getGoalType(d?.inputA?.goal)}
                                                isCurrency={true}
                                                value={d?.inputA?.value}
                                                disabled={readOnly}
                                                selected={d?.inputA?.selected}
                                                onClick={() => handleGoalSelect(d?.inputA)}
                                                onChange={(event) => handleGoalValueChange(d?.inputA, event)}
                                                symbol={d?.symbolA}
                                                onBlur={handleBlur}
                                                invalid={errorValidationCampaignGoal}
                                            />
                                            <Spacer height={0} width={16} />
                                            <WeeklyBudgetDropdown
                                                key={d?.inputB?.goal}
                                                id={`${d?.inputB?.goal.toLowerCase()}-input`}
                                                title={getGoalType(d?.inputB?.goal)}
                                                values={d?.inputB?.values ?? []}
                                                totalBudget={campaign.budget}
                                                onValueChange={(event) => handleGoalValueChange(d?.inputB, event)}
                                                symbol={d?.symbolB}
                                                disabled={readOnly}
                                                selected={d?.inputB?.selected}
                                                onClick={() => handleGoalSelect(d?.inputB)}
                                                invalid={errorValidationCampaignGoal}
                                                generateBudgetsArray={generateBudgetsArray}
                                                onApplyChanges={(budgets) => handleBudgetChanges(d?.inputB, budgets)}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <RadioInput
                                                key={d?.inputA?.goal}
                                                id={`${d?.inputA?.goal.toLowerCase()}-input`}
                                                name={d?.inputA?.goal}
                                                title={getGoalType(d?.inputA?.goal)}
                                                value={d?.inputA?.value}
                                                disabled={readOnly}
                                                selected={d?.inputA?.selected}
                                                onClick={() => handleGoalSelect(d?.inputA)}
                                                onChange={(event) => handleGoalValueChange(d?.inputA, event)}
                                                symbol={d?.symbolA}
                                                onBlur={handleBlur}
                                                invalid={errorValidationCampaignGoal}
                                            />

                                            <Spacer height={0} width={16} />
                                            <RadioInput
                                                key={d?.inputB?.goal}
                                                id={`${d?.inputB?.goal.toLowerCase()}-input`}
                                                name={d?.inputB?.goal}
                                                title={getGoalType(d?.inputB?.goal)}
                                                value={d?.inputB?.value}
                                                disabled={readOnly}
                                                selected={d?.inputB?.selected}
                                                onClick={() => handleGoalSelect(d?.inputB)}
                                                onChange={(event) => handleGoalValueChange(d?.inputB, event)}
                                                symbol={d?.symbolB}
                                                onBlur={handleBlur}
                                                invalid={errorValidationCampaignGoal}
                                            />
                                        </>
                                    )}
                                </Flex>
                            </LabelledSection>
                        );
                    })}
                </AutoFlex>
            </div>
        </div>
    );
};

export default PlanningGoals;
